import { useRef, useEffect } from "react";
import { postVoiceEvent } from "../../api.js";
import { getHeadersWithUniqueVisitorId } from "../../../Auth/index.js";
const DEFAULT_INTERVAL_MS = 5e3;
function useWebRtcStats(params) {
  const { conversationId, pc, enabled, intervalMs = DEFAULT_INTERVAL_MS } = params;
  const cancelledRef = useRef({ flag: false });
  const authHeadersRef = useRef({});
  useEffect(() => {
    if (!enabled || !conversationId || !pc) return;
    const cancelled = { flag: false };
    cancelledRef.current = cancelled;
    getHeadersWithUniqueVisitorId().then((h) => {
      if (!cancelled.flag) authHeadersRef.current = h;
    }).catch(() => {
    });
    const inbound = { lastPacketsLost: 0, lastPacketsReceived: 0, lastBytes: 0, lastTs: 0 };
    const outbound = { lastPacketsLost: 0, lastPacketsReceived: 0, lastBytes: 0, lastTs: 0 };
    const sample = async () => {
      if (cancelled.flag) return;
      const connState = pc.connectionState;
      if (connState !== "connected") return;
      let report;
      try {
        report = await pc.getStats();
      } catch {
        return;
      }
      if (cancelled.flag) return;
      const inboundRow = pickAudioRow(report, "inbound-rtp");
      const outboundRow = pickAudioRow(report, "outbound-rtp");
      const candidatePair = pickActiveCandidatePair(report);
      const remoteInboundRow = pickRemoteInbound(report);
      const nowMs = Date.now();
      const computeRate = (acc, row) => {
        if (!row || row.bytes === void 0) return void 0;
        if (acc.lastTs === 0) {
          acc.lastBytes = row.bytes;
          acc.lastTs = row.ts;
          return void 0;
        }
        const dtSec = Math.max(1e-3, (row.ts - acc.lastTs) / 1e3);
        const dBytes = Math.max(0, row.bytes - acc.lastBytes);
        acc.lastBytes = row.bytes;
        acc.lastTs = row.ts;
        return Math.round(dBytes * 8 / dtSec);
      };
      const computeDeltas = (acc, row) => {
        if (!row) return { dLost: 0, dRecv: 0 };
        const dLost = Math.max(0, (row.packetsLost ?? 0) - acc.lastPacketsLost);
        const dRecv = Math.max(0, (row.packetsReceived ?? 0) - acc.lastPacketsReceived);
        acc.lastPacketsLost = row.packetsLost ?? acc.lastPacketsLost;
        acc.lastPacketsReceived = row.packetsReceived ?? acc.lastPacketsReceived;
        return { dLost, dRecv };
      };
      const bitrateIn = computeRate(inbound, inboundRow);
      const bitrateOut = computeRate(outbound, outboundRow);
      const inDeltas = computeDeltas(inbound, inboundRow);
      const outDeltas = computeDeltas(outbound, outboundRow);
      const payload = {
        rttMs: candidatePair && typeof candidatePair.currentRoundTripTimeSec === "number" ? Math.round(candidatePair.currentRoundTripTimeSec * 1e3) : remoteInboundRow && typeof remoteInboundRow.roundTripTimeSec === "number" ? Math.round(remoteInboundRow.roundTripTimeSec * 1e3) : void 0,
        jitterMs: (inboundRow == null ? void 0 : inboundRow.jitterSec) !== void 0 ? Math.round(inboundRow.jitterSec * 1e3) : void 0,
        packetsLostIn: inDeltas.dLost,
        packetsReceivedIn: inDeltas.dRecv,
        packetsLostOut: outDeltas.dLost,
        packetsReceivedOut: outDeltas.dRecv,
        bitrateIn,
        bitrateOut,
        audioLevelIn: inboundRow == null ? void 0 : inboundRow.audioLevel,
        audioLevelOut: outboundRow == null ? void 0 : outboundRow.audioLevel
      };
      postVoiceEvent({
        conversationId,
        events: [{ type: "webrtc.stats", payload, t: nowMs }],
        headersToInject: authHeadersRef.current
      });
    };
    void sample();
    const handle = setInterval(() => {
      void sample();
    }, intervalMs);
    return () => {
      cancelled.flag = true;
      clearInterval(handle);
    };
  }, [enabled, conversationId, pc, intervalMs]);
}
function pickAudioRow(report, direction) {
  let row = null;
  report.forEach((s) => {
    if (s.type !== direction) return;
    if (s.kind && s.kind !== "audio") return;
    if (s.mediaType && s.mediaType !== "audio") return;
    row = {
      packetsLost: typeof s.packetsLost === "number" ? s.packetsLost : void 0,
      packetsReceived: typeof s.packetsReceived === "number" ? s.packetsReceived : void 0,
      jitterSec: typeof s.jitter === "number" ? s.jitter : void 0,
      bytes: typeof s.bytesReceived === "number" ? s.bytesReceived : typeof s.bytesSent === "number" ? s.bytesSent : void 0,
      audioLevel: typeof s.audioLevel === "number" ? s.audioLevel : void 0,
      ts: typeof s.timestamp === "number" ? s.timestamp : Date.now()
    };
  });
  return row;
}
function pickActiveCandidatePair(report) {
  let pair = null;
  report.forEach((s) => {
    if (s.type !== "candidate-pair") return;
    if (!s.nominated && pair) return;
    if (s.state && s.state !== "succeeded") return;
    pair = s;
  });
  if (!pair) return null;
  return { currentRoundTripTimeSec: pair.currentRoundTripTime };
}
function pickRemoteInbound(report) {
  let row = null;
  report.forEach((s) => {
    if (s.type !== "remote-inbound-rtp") return;
    if (s.kind && s.kind !== "audio") return;
    row = s;
  });
  if (!row) return null;
  return { roundTripTimeSec: row.roundTripTime };
}
export {
  useWebRtcStats
};
//# sourceMappingURL=useWebRtcStats.js.map
